/* ── Agent Persona Definitions ── */

/**
 * `remi`, `cole`, and `maestro` are retained for marketing-site landing pages
 * and admin/console historical displays. They have empty `workflowSlugs` and
 * are not routable runtime targets — DB-authored definitions are archived
 * (see backend migration `0087_archive_deprecated_companion_agents`).
 */
export type AgentId = "cipher" | "operator" | "remi" | "nova" | "maestro" | "cole" | "saga";

const R2 = "https://pub-56b94dd8a0c946a689522e687290e19d.r2.dev";

export interface AgentPersona {
  name: string;
  tagline: string;
  colorHex: string;
  workflowSlugs: string[];
  /** Capabilities / services showcase video (slide 3 style) */
  video: string;
  /** Human persona */
  humanName: string;
  humanRole: string;
  humanSalary: string;
  /** Portrait video of the human-looking employee (slide 2 style) */
  humanVideo: string;
  /** Companion TTS voice key; legacy ElevenLabs IDs map to Google Chirp voices in Core. */
  voiceId: string;
  /** HeyGen Streaming Avatar ID (fixed avatar model) */
  heyGenAvatarId?: string;
  /** Simli face ID for lip-synced avatar */
  simliFaceId?: string;
  /** LiveAvatar avatar ID (LITE mode) */
  liveAvatarId?: string;
}

export const AGENT_PERSONAS: Record<AgentId, AgentPersona> = {
  cipher: {
    name: "EMAIL & CREATIVE",
    tagline: "Email Marketing & Creative Studio",
    colorHex: "#ec4899",
    workflowSlugs: ["social-copilot"],
    video: `${R2}/Data-Ops%20Services(slide3)long1080p.mp4`,
    humanName: "Skylar",
    humanRole: "Email & Creative Strategist",
    humanSalary: "$85,000/yr",
    humanVideo: `${R2}/Gen%20Z%20Marketing%20Loop%20(slide2)1080p.mp4`,
    voiceId: "7PNtRIyJr9ToSuEY2yra",
    heyGenAvatarId: "josh_lite3_20230714", // Male, verified interactive avatar
    simliFaceId: "f027c0f0-0b56-492a-939e-ca51aae3a8b9",
  },
  operator: {
    name: "PERFORMANCE MARKETING",
    tagline: "Ads, SEO & GEO",
    colorHex: "#10b981",
    workflowSlugs: [
      "mktg-audience-sync",
      "mktg-campaign-analytics",
      "mktg-cross-platform",
      "growth-copilot",
      "marcus-video-editor",
    ],
    video: `${R2}/Social%20Manager%20Services%20Loop_1080p.mp4`,
    humanName: "Marcus",
    humanRole: "Growth Manager",
    humanSalary: "$78,000/yr",
    humanVideo: `${R2}/Gen%20Z%20Social%20Loop%20(slide2).mp4`,
    // Maps to Google Chirp3-HD "Puck" (upbeat young creator male) via BUILTIN_GOOGLE_VOICE_MAP
    // in core/src/platform/providers/tts/provider.ts. Must stay aligned with
    // core/src/platform/config/agents.ts and core/src/config/agents.ts.
    voiceId: "pNInz6obpgDQGcFmaJgB",
    heyGenAvatarId: "Wayne_20240711", // Male, verified interactive avatar
    simliFaceId: "2b8a8e10-34bb-4189-bbbc-f1a4fe2b0c26",
    liveAvatarId: "64b526e4-741c-43b6-a918-4e40f3261c7a",
  },
  remi: {
    name: "PRODUCTS & STORE",
    tagline: "Products, Store Design & Brand Identity",
    colorHex: "#f472b6",
    workflowSlugs: [],
    video: `${R2}/Marketing%20Wizard%20Services%20Loop_1080p.mp4`,
    humanName: "Remi",
    humanRole: "Store & Brand Manager",
    humanSalary: "$72,000/yr",
    humanVideo: `${R2}/Gen%20Z%20Content%20Director%20(slide2)1080p.mp4`,
    voiceId: "pFZP5JQG7iQjIQuC4Bku",
    simliFaceId: "4fe66566-8402-48d8-942f-b240893a9464",
    liveAvatarId: "4fe66566-8402-48d8-942f-b240893a9464",
  },
  nova: {
    name: "CONTENT PUBLISHING",
    tagline: "Editorial Intelligence & Content Pipeline",
    colorHex: "#6366f1",
    workflowSlugs: ["content-studio-copilot", "content-studio-setup", "content-studio-add-source", "content-studio-fetch-sources", "content-studio-auto-fetch", "content-studio-extract-publish"],
    video: "",
    humanName: "Nova",
    humanRole: "Content Editor",
    humanSalary: "$82,000/yr",
    humanVideo: "",
    voiceId: "pFZP5JQG7iQjIQuC4Bku",
    simliFaceId: "4fe66566-8402-48d8-942f-b240893a9464",
    liveAvatarId: "4fe66566-8402-48d8-942f-b240893a9464",
  },
  maestro: {
    name: "MAESTRO",
    tagline: "Global Orchestrator & Command Center",
    colorHex: "#8b5cf6",
    workflowSlugs: [],
    video: "",
    humanName: "Maestro",
    humanRole: "Chief Strategist",
    humanSalary: "",
    humanVideo: "",
    voiceId: "",
  },
  cole: {
    name: "FORGE",
    tagline: "Pitch Coach — Cole helps you forge an investor-ready deck",
    colorHex: "#f59e0b",
    workflowSlugs: [],
    video: "",
    humanName: "Cole",
    humanRole: "Pitch Coach",
    humanSalary: "",
    humanVideo: "",
    voiceId: "",
  },
  saga: {
    name: "SOCIAL STUDIO",
    tagline: "Story arcs, hook types, and posting cadence",
    colorHex: "#a855f7",
    workflowSlugs: ["social-studio-copilot"],
    video: "",
    humanName: "Saga",
    humanRole: "Social Creative Director",
    humanSalary: "$92,000/yr",
    humanVideo: "",
    voiceId: "EXAVITQu4vr4xnSDxMaL",
  },
};
